import { useParams } from 'react-router-dom'
import moment from 'moment'

import { selectPayrollByUuid } from '../payroll.selectors'
import { formatCurrency } from '@/utils/currencyHelpers'
import { Link, Text, Card, Separator, CardColors } from '@/ui'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { Gusto_Payroll } from '../generated_gusto_types'
import { ContactSupport } from './ContactSupport'
import { TypographyStyles } from '@/styles'
import { PayrollDetails } from '../run-payroll/employee-payroll/PayrollDetails'

const formatDate = (date: string) =>
  moment(date).format(DATE_FORMATS.DISPLAY_SHORT)

const PayrollOverview = ({ payroll }: { payroll: Gusto_Payroll | null }) => {
  if (!payroll) {
    return null
  }

  const {
    check_date,
    pay_period: { start_date, end_date },
    processed_date,
  } = payroll

  const companyDebit = payroll.totals?.company_debit || '0'

  return (
    <Card color={CardColors.EMPHASIS}>
      <Text variant={TypographyStyles.H1}>Payroll Details</Text>

      <Separator />

      <div className="flex justify-between">
        <Text variant={TypographyStyles.BODY_MD}>
          {' '}
          Funds Debited: {formatCurrency(companyDebit)}
        </Text>
      </div>

      <Separator className="my-2" />

      <div className="flex justify-between">
        <Text variant={TypographyStyles.BODY_MD}>
          Pay Date: {formatDate(check_date)}
        </Text>
      </div>

      <Separator className="my-2" />

      <div className="flex">
        <Text variant={TypographyStyles.BODY_MD}>Pay Period:</Text>
        {start_date && end_date && (
          <Text variant={TypographyStyles.BODY_MD}>
            {formatDate(start_date)} - {formatDate(end_date)}
          </Text>
        )}
      </div>

      <Separator className="my-2" />

      <div className="flex justify-between">
        <Text variant={TypographyStyles.BODY_MD}>
          <b>Processed on: </b>
          {processed_date ? formatDate(processed_date) : ''}
        </Text>
      </div>
    </Card>
  )
}

export const PayrollHistoryDetails = () => {
  const { uuid } = useParams()
  const payroll = useReselector(selectPayrollByUuid, uuid)

  return (
    <Card color={CardColors.LEVEL_0}>
      <Text variant={TypographyStyles.H1}>Payroll History</Text>

      <div className="flex flex-row items-start gap-2">
        <Link href="/payroll/history">Payroll History</Link>
        {payroll?.pay_period.start_date && payroll?.pay_period.end_date && (
          <Text className="pt-[3px]">
            <b>
              &nbsp;&rsaquo;&nbsp;Period:{' '}
              {moment(payroll.pay_period.start_date).format(
                DATE_FORMATS.MONTH_DAY
              )}{' '}
              - {formatDate(payroll.pay_period.end_date)}
            </b>
          </Text>
        )}
      </div>

      <Separator />

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-7/12">
          <PayrollOverview payroll={payroll} />
        </div>
        <div className="lg:w-5/12">
          <ContactSupport />
        </div>
      </div>

      <Separator />

      {uuid && <PayrollDetails uuid={uuid} title="Additional Details" />}
    </Card>
  )
}
